import {
  CREATOR_TABS,
  DISABLED_CREATOR_OPTION_TOOLTIP,
  CREATOR_INVITATION_STATUS_LABELS,
  CREATOR_INVITATION_STATUS_COLORS,
  DASHBOARD_CREATOR_CHANNELS,
  CREATOR_STATUS,
} from '@/app/dashboards/reportConstants';
import { CHANNELS } from '@/models/dashboards/channels.enum';
import { useCreatorsStore } from '@/stores/creators';
import { computed } from 'vue';
import { inviteStatusDetails, TTCM_STATUS_OPTIONS } from '@/app/creators/constants';

export function useCreatorOptions({ channels, selectedBrandIds }) {
  const creatorsStore = useCreatorsStore();

  // Helper functions for creator data transformation
  function isInviteAcceptedAndPlatformConnected(sourceAccount) {
    if (!sourceAccount) return false;
    return (
      sourceAccount?.invitationStatus === inviteStatusDetails.ACCEPTED.value &&
      sourceAccount?.isConnectionValid
    );
  }

  function getCreatorStatus(primaryAccount) {
    if (isInviteAcceptedAndPlatformConnected(primaryAccount)) {
      return {
        status: CREATOR_STATUS.CONNECTED,
        color: CREATOR_INVITATION_STATUS_COLORS.ACCEPTED,
      };
    }
    // if invite accepted but platform connection disconnected
    if (primaryAccount.invitationStatus === inviteStatusDetails.ACCEPTED.value) {
      return {
        status: CREATOR_STATUS.NOT_CONNECTED,
        color: CREATOR_INVITATION_STATUS_COLORS.default,
      };
    }
    return {
      status:
        CREATOR_INVITATION_STATUS_LABELS?.[primaryAccount?.invitationStatus] ??
        CREATOR_INVITATION_STATUS_LABELS.default,
      color:
        CREATOR_INVITATION_STATUS_COLORS[primaryAccount?.invitationStatus] ??
        CREATOR_INVITATION_STATUS_COLORS.default,
    };
  }

  function transformCreatorData(relationshipId, { accounts = {} }, brandId = null) {
    const transformedCreators = [];
    DASHBOARD_CREATOR_CHANNELS.forEach((channel) => {
      if (accounts[channel]) {
        transformedCreators.push({
          label: accounts[channel].handle,
          value: parseInt(relationshipId, 10),
          avatar: { image: accounts[channel].avatar },
          imgSrc: accounts[channel].avatar,
          invitationStatus: accounts[channel]?.invitationStatus,
          isConnectionValid: accounts[channel]?.isConnectionValid,
          status: getCreatorStatus(accounts[channel]),
          relationshipId: parseInt(relationshipId, 10),
          source: channel,
          ...(channel === 'tiktok' ? { ttcmStatus: accounts[channel]?.ttcmStatus } : {}),
          ...(brandId ? { brandId } : {}),
        });
      }
    });
    return transformedCreators;
  }

  // Transform multiBrandCreators into reportCreatorOptions format
  const reportCreatorOptions = computed(() => {
    const allCreators = [];
    Object.entries(creatorsStore.multiBrandCreators).forEach(([brandId, brandCreators]) => {
      Object.entries(brandCreators).forEach(([relationshipId, creatorData]) => {
        allCreators.push([relationshipId, creatorData, brandId]);
      });
    });
    return allCreators.flatMap(([relationshipId, creatorData, brandId]) =>
      transformCreatorData(relationshipId, creatorData, brandId),
    );
  });

  // Transform multiBrandRelationshipTags into reportCreatorTagOptions format
  const reportCreatorTagOptions = computed(() => {
    const tagsData = creatorsStore.multiBrandRelationshipTags;

    return Object.entries(tagsData).map(([tagId, tagData]) => {
      const creatorsData = tagData.creators || {};
      const transformedCreators = Object.entries(creatorsData).flatMap(
        ([relationshipId, creatorData]) => transformCreatorData(relationshipId, creatorData),
      );

      return {
        label: tagData.name,
        value: parseInt(tagId, 10),
        color: tagData.color,
        creators: transformedCreators,
        count: transformedCreators.length,
        brandId: tagData.brandId,
      };
    });
  });

  function filterCreatorsByPlatform(creators, platform) {
    return creators.filter((creator) => creator.source && creator.source === platform);
  }

  function filterCreatorTagsByChannel(availableCreatorTags, platform) {
    return availableCreatorTags
      .map((tag) => ({
        ...tag,
        creators: filterCreatorsByPlatform(tag.creators, platform),
        count: tag.creators.filter((creator) => creator.source && creator.source === platform)
          .length,
      }))
      .filter((tag) => tag.count > 0);
  }

  // Support multi channel for dashboard template form. One channel is necessary in report form
  function getCreatorOptionsForChannels(creators, availableCreatorTags, availableChannels) {
    let allFilteredCreators = [];
    let allFilteredCreatorTags = [];
    const platforms = new Set();

    availableChannels.forEach((channel) => {
      if (channel === CHANNELS.TIKTOK_UGC.value) {
        platforms.add('tiktok');
      } else if (
        channel === CHANNELS.INSTAGRAM_UGC.value ||
        channel === CHANNELS.INSTAGRAM_STORIES_UGC.value
      ) {
        platforms.add('instagram');
      }
    });

    if (platforms.size === 0) {
      allFilteredCreators = creators;
      allFilteredCreatorTags = availableCreatorTags;
    } else {
      platforms.forEach((platform) => {
        const platformCreators = filterCreatorsByPlatform(creators, platform);
        const platformCreatorTags = filterCreatorTagsByChannel(availableCreatorTags, platform);

        allFilteredCreators = allFilteredCreators.concat(platformCreators);
        allFilteredCreatorTags = allFilteredCreatorTags.concat(platformCreatorTags);
      });
    }

    // Remove duplicates based on value for multi brand/channel
    allFilteredCreators = allFilteredCreators.filter(
      (creator, index, self) => index === self.findIndex((c) => c.value === creator.value),
    );
    allFilteredCreatorTags = allFilteredCreatorTags.filter(
      (tag, index, self) => index === self.findIndex((t) => t.value === tag.value),
    );

    return {
      [CREATOR_TABS.CREATORS]: allFilteredCreators.sort((a, b) =>
        a.label ? a.label.localeCompare(b.label) : a,
      ),
      [CREATOR_TABS.CREATOR_TAGS]: allFilteredCreatorTags.sort((a, b) =>
        a.label ? a.label?.localeCompare(b.label) : a,
      ),
    };
  }

  function validateCreatorOption(sourceAccount) {
    // disable tiktok creator source account option if it is not connected nor in TTCM
    const { invitationStatus, source, ttcmStatus } = sourceAccount;
    const disabled = !!(
      source === 'tiktok' &&
      ttcmStatus !== TTCM_STATUS_OPTIONS.IN &&
      invitationStatus !== inviteStatusDetails.ACCEPTED.value
    );
    const tooltip = disabled ? DISABLED_CREATOR_OPTION_TOOLTIP : undefined;
    return { ...sourceAccount, disabled, tooltip };
  }

  const creatorOptions = computed(() => {
    const validatedCreatorOptions = reportCreatorOptions.value
      .filter((creator) => selectedBrandIds?.value?.includes(Number(creator?.brandId)))
      .map((creator) => validateCreatorOption(creator));

    const validatedCreatorTagOptions = reportCreatorTagOptions.value.filter((creator) =>
      selectedBrandIds?.value?.includes(Number(creator?.brandId)),
    );

    return getCreatorOptionsForChannels(
      validatedCreatorOptions,
      validatedCreatorTagOptions,
      channels.value || [],
    );
  });

  return {
    creatorOptions,
    reportCreatorOptions,
    reportCreatorTagOptions,
  };
}
